import { captureMessage, withScope } from '@sentry/react';

const JSON_MIME = 'application/json';

export interface ResponseDetails {
    status: number;
    type: string;
    text: string;
    headers: Record<string, string>;
    url?: string;
    readyState?: number;
}

export function isExpectedResponse(
    req: { url: string; method: string },
    res: ResponseDetails,
): boolean {
    if (res.status < 200 || res.status >= 300 || res.status === 204) {
        return true;
    }
    if (res.type !== JSON_MIME) {
        reportForeignResponse(req, res);
        return false;
    }
    if (res.text === '' && res.headers['content-length'] !== '0') {
        reportEmptyJsonResponse(req, res);
    }
    return true;
}

const ID_SEGMENT =
    /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// Query strings are dropped everywhere here: they can carry tokens, and the
// query of a portal redirect embeds the originally requested URL.
function routeOf(url: string): string {
    return new URL(url, window.location.origin).pathname
        .split('/')
        .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
        .join('/');
}

function originAndPathOf(url: unknown): string | null {
    if (typeof url !== 'string') {
        return null;
    }
    try {
        const parsed = new URL(url);
        return parsed.origin + parsed.pathname;
    } catch {
        return null;
    }
}

function titleOf(html: string): string | null {
    const match = /<title[^>]*>([^<]*)<\/title>/i.exec(html);
    return match ? match[1].trim().slice(0, 200) : null;
}

function reportForeignResponse(
    req: { url: string; method: string },
    res: ResponseDetails,
): void {
    const text = typeof res.text === 'string' ? res.text : '';
    withScope(scope => {
        scope.setLevel('warning');
        scope.setTags({
            'foreign_response.route': routeOf(req.url),
            'foreign_response.method': req.method,
            'foreign_response.status': String(res.status),
            'foreign_response.content_type': res.type || 'none',
        });
        scope.setExtras({
            // Where the browser actually ended up after following redirects;
            // for a captive portal this is the portal's host.
            responseUrl: originAndPathOf(res.url),
            responseHeaders: res.headers,
            title: titleOf(text),
            bodySnippet: text.slice(0, 1000),
            navigatorOnline: navigator.onLine,
        });
        captureMessage('Non-JSON 2xx response treated as failed request');
    });
}

// When the page last came back to the foreground, to correlate empty bodies
// with requests that were in flight while the page or app was suspended.
let lastBecameVisibleAt = performance.now();
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
        lastBecameVisibleAt = performance.now();
    }
});

// Reporting every empty answer once flooded Sentry with two events per second;
// whatever the assumptions below turn out to be worth, a page reports at most
// this many.
const MAX_EMPTY_JSON_REPORTS_PER_PAGE = 10;
let emptyJsonReports = 0;

/**
 * An empty body with a JSON content type is legitimate for `Ok(null)`
 * endpoints, but the consumers of endpoints that never answer empty still
 * crash on it, exclusively on WebKit, now that non-JSON answers are filtered
 * out (Sentry CUSTOMER-PROD-D8Q, -D8W, -D8Y). The hooks cannot tell the two
 * apart, so this only reports, tagged by route. An `Ok(null)` arrives with
 * `Content-Length: 0` and is not reported: in production every one of the
 * first 700 reports was such an answer and none came from a route that
 * crashed. A `content-length` that does not match the empty body means the
 * browser dropped the body; a hidden page or a recent return to the
 * foreground means the request was in flight while the page or app was
 * suspended.
 */
function reportEmptyJsonResponse(
    req: { url: string; method: string },
    res: ResponseDetails,
): void {
    if (emptyJsonReports >= MAX_EMPTY_JSON_REPORTS_PER_PAGE) {
        return;
    }
    emptyJsonReports++;
    withScope(scope => {
        scope.setLevel('warning');
        scope.setTags({
            'empty_response.route': routeOf(req.url),
            'empty_response.method': req.method,
            'empty_response.status': String(res.status),
            'empty_response.content_length':
                res.headers['content-length'] ?? 'none',
            'empty_response.visibility': document.visibilityState,
        });
        scope.setExtras({
            responseUrl: originAndPathOf(res.url),
            responseHeaders: res.headers,
            xhrReadyState: res.readyState,
            msSinceBecameVisible: Math.round(
                performance.now() - lastBecameVisibleAt,
            ),
            navigatorOnline: navigator.onLine,
        });
        captureMessage('Empty JSON 2xx response');
    });
}
